import { FC, useEffect, useMemo, useState } from 'react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Heading } from '@/components/typography';
import { useTranslation } from 'react-i18next';
import { Team } from '@/types/types';
import { VacanciesFilter } from './Filter';
import { VacancyCard } from '@/components/cards/VacancyCard';
import { paginate } from '@/utils/paginate';
import { Pagination } from '@/components/navigation/Pagination';
import { removeLastTrailingSlash } from '@/utils/removeLastTrailingSlash';
import { useRouter } from 'next/router';

interface SyncSlider {
  language: string;
  className?: string;
  sliderToShow?: number;
  vacanciesList: Team[];
}

export const Vacancies: FC<SyncSlider> = ({ vacanciesList }) => {
  const { t } = useTranslation('common');
  const title = t('vacanciesTitle');
  const notFound = t('vacanciesNotFound');

  const router = useRouter();
  const [queryPage, setQueryPage] = useState(Number(router.query.page) || 1);

  const selectedCities = useMemo(() => {
    const cityQuery = router.query.city;
    if (!cityQuery) return [];
    return Array.isArray(cityQuery) ? cityQuery : [cityQuery];
  }, [router.query.city]);

  const selectedTypes = useMemo(() => {
    const typeQuery = router.query.type;
    if (!typeQuery) return [];
    return Array.isArray(typeQuery) ? typeQuery : [typeQuery];
  }, [router.query.type]);

  const selectedFormats = useMemo(() => {
    const formatQuery = router.query.format;
    if (!formatQuery) return [];
    return Array.isArray(formatQuery) ? formatQuery : [formatQuery];
  }, [router.query.format]);

  useEffect(() => {
    if (router.query.page) {
      setQueryPage(Number(router.query.page));
    }
  }, [router.query.page]);

  const citiesOptions = useMemo(() => {
    const citiesSet = new Set<string>();
    vacanciesList.forEach(v => {
      v.teamCities?.edges.forEach(edge => citiesSet.add(edge.node.name));
    });
    return Array.from(citiesSet).map(name => ({ label: name, value: name }));
  }, [vacanciesList]);

  const typesOptions = useMemo(() => {
    const typesSet = new Set<string>();
    vacanciesList.forEach(v => {
      v.teamTypes?.edges.forEach(edge => typesSet.add(edge.node.name));
    });
    return Array.from(typesSet).map(name => ({ label: name, value: name }));
  }, [vacanciesList]);

  const formatsOptions = useMemo(() => {
    const formatsSet = new Set<string>();
    vacanciesList.forEach(v => {
      v.teamFormats?.edges.forEach(edge => formatsSet.add(edge.node.name));
    });
    return Array.from(formatsSet).map(name => ({ label: name, value: name }));
  }, [vacanciesList]);

  const filteredVacancies = useMemo(() => {
    return vacanciesList.filter(item => {
      const cityNames =
        item.teamCities?.edges.map(edge => edge.node.name) || [];
      const typeNames = item.teamTypes?.edges.map(edge => edge.node.name) || [];
      const formatNames =
        item.teamFormats?.edges.map(edge => edge.node.name) || [];

      const cityMatch =
        selectedCities.length === 0 ||
        selectedCities.some(c => cityNames.includes(c));
      const typeMatch =
        selectedTypes.length === 0 ||
        selectedTypes.some(t => typeNames.includes(t));
      const formatMatch =
        selectedFormats.length === 0 ||
        selectedFormats.some(f => formatNames.includes(f));

      return cityMatch && typeMatch && formatMatch;
    });
  }, [vacanciesList, selectedCities, selectedTypes, selectedFormats]);

  const PAGE_SIZE = 15;
  const totalPages = Math.ceil(filteredVacancies.length / PAGE_SIZE);

  return (
    <div className="vacancies" id="vacancies">
      <Heading tag="h2" className="vacancies__title mb-4" content={title} />

      <VacanciesFilter
        citiesOptions={citiesOptions}
        typesOptions={typesOptions}
        formatsOptions={formatsOptions}
        selectedCities={selectedCities}
        selectedTypes={selectedTypes}
        selectedFormats={selectedFormats}
      />

      <div>
        {filteredVacancies.length > 0 ? (
          <ul className="grid gap-4 lg:justify-center">
            {paginate(filteredVacancies, queryPage, PAGE_SIZE).map(item => {
              const teamCities: string[] =
                item.teamCities?.edges?.map(
                  (edge: { node: { name: string } }) => edge?.node?.name,
                ) ?? [];

              const cities =
                selectedCities.length > 0
                  ? teamCities.filter(city => selectedCities.includes(city))
                  : teamCities;

              return (
                <li key={item.id} className="md:h-full">
                  <VacancyCard
                    title={item.title}
                    linkUrl={removeLastTrailingSlash(item.uri)}
                    isInnerLink={true}
                    vacancies={item.vacancies}
                    cities={cities ?? []}
                    format={item.teamFormats?.edges[0]?.node?.name ?? ''}
                    type={item.teamTypes?.edges[0]?.node?.name ?? ''}
                  />
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-center">{notFound}</p>
        )}
        {totalPages > 1 && (
          <Pagination
            currentPage={queryPage}
            totalPages={totalPages}
            className="mt-8 md:mt-[32px]"
          />
        )}
      </div>
    </div>
  );
};
